// Subtabla de líneas de un pedido de venta.
//
// Tiene dos formas según de dónde venga la fila:
//   · Fabric  → completa: bandas de factura, qty servida, importes, adjuntos.
//   · Overlay → simple: el pedido acaba de crearse en F&O y Fabric aún no lo
//               tiene, así que no existen ni facturas ni cantidades servidas.
import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { pedidosVentasApi } from '@/services/api'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { usePedidoVentaDetalle } from '@/hooks/usePedidoVentaDetalle'
import { Loader } from '@/components/common/Loader'
import { SubTableWrapper } from '@/components/common/SubTableWrapper'
import { SubTotalsRow } from '@/components/common/SubTotalsRow'
import { PickingBar } from '@/components/common/PickingBar'
import { CabeceraMarca, FilaMarcable, MarcaCelda } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { predicadoArticulos } from '@/utils/filtroArticulo'
import { columnasPieVenta } from '@/utils/subtotalesLineas'
import type {
  FiltroEstadoCarga, MarcasFilas, PedidoVentaLinea, PedidoVentaLineaReciente,
} from '@/types'
import { CargasPedido } from './CargasPedido'
import { FacturasPedido } from './FacturasPedido'
import { pendienteTrasFactura } from '@/utils/facturasPedido'
import {
  fmtDate, fmtN, fmtQty, fmtEur, fmtPct, ESTADO_COLOR, TOTAL_COLS, filaResaltada,
} from './formato'

interface Props {
  salesid: string
  dark: boolean
  /** Marcas del usuario, en el ámbito de la lista entera. */
  marcas: MarcasFilas
  /** Clave de la fila del pedido. La de cualquier sublínea de este acordeón
   *  arranca por ella, así que la línea 3 de un pedido y la 3 de otro son claves
   *  distintas y una marca nunca se contagia entre pedidos. */
  clavePedido: string
  /** La fila viene del overlay de Dataverse: aún no está en Fabric. */
  pendienteSync?: boolean
  /** Términos del buscador de la lista, combinados con OR. Si alguno casa con
   *  un artículo del pedido, sus facturas y cargas arrancan desplegadas y las
   *  filas coincidentes van resaltadas. El overlay no lo usa: no tiene ni
   *  facturas ni cargas. */
  terminos?: string[]
  onVerAdjuntosFactura: (recid: string, factura: string) => void
  /** El pedido admite cargas nuevas (está abierto). Un pedido facturado o
   *  cancelado ya no se carga, así que el bloque sale de sólo lectura. */
  puedeCrearCarga?: boolean
  /** Estado de carga elegido en el filtro de arriba. */
  filtroEstadoCarga?: FiltroEstadoCarga
}

export function LineasRow({
  salesid, dark, marcas, clavePedido, pendienteSync, terminos, onVerAdjuntosFactura,
  puedeCrearCarga, filtroEstadoCarga,
}: Props) {
  return pendienteSync
    ? <LineasOverlay salesid={salesid} dark={dark} marcas={marcas} clavePedido={clavePedido} />
    : <LineasFabric
        salesid={salesid}
        dark={dark}
        marcas={marcas}
        clavePedido={clavePedido}
        terminos={terminos}
        onVerAdjuntosFactura={onVerAdjuntosFactura}
        puedeCrearCarga={puedeCrearCarga}
        filtroEstadoCarga={filtroEstadoCarga}
      />
}

// ── Overlay: pedido recién creado, sin consolidar en Fabric ───────────────────

function LineasOverlay({ salesid, dark, marcas, clavePedido }: {
  salesid: string
  dark: boolean
  marcas: MarcasFilas
  clavePedido: string
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['venta-lineas-recientes', salesid],
    queryFn: () => pedidosVentasApi.getLineasRecientes(salesid),
    staleTime: 60 * 1000,
  })
  const lineas: PedidoVentaLineaReciente[] = (data?.data ?? []) as PedidoVentaLineaReciente[]

  const subTh = `px-2 py-1.5 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`
  const subTd = `px-2 py-1.5 text-[11px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const mutedTxt = dark ? 'text-slate-500' : 'text-slate-400'
  const divider = dark ? 'border-white/4' : 'border-slate-100'

  if (isLoading) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <Loader label={t('salesOrders.line.loading')} size={10} className="py-3" />
    </SubTableWrapper>
  )

  if (isError) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-10 py-3">
        <span className={`text-[11px] ${mutedTxt}`}>{t('salesOrders.sync.linesPending')}</span>
      </div>
    </SubTableWrapper>
  )

  if (lineas.length === 0) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-10 py-3">
        <span className={`text-[11px] ${mutedTxt}`}>{t('salesOrders.line.none')}</span>
      </div>
    </SubTableWrapper>
  )

  return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-8 pr-4 py-2 overflow-x-auto">
        <div className={`mb-2 inline-flex items-center gap-1.5 text-[10px] px-2 py-1 rounded ${
          dark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700'
        }`}>
          <svg className="w-3 h-3 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="8" r="6.5" /><path d="M8 5v3.5l2 1.5" />
          </svg>
          {t('salesOrders.sync.linesNotice')}
        </div>
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className={`border-b ${divider}`}>
              <CabeceraMarca />
              <th className={subTh}>{t('salesOrders.line.lineNum')}</th>
              <th className={subTh}>{t('salesOrders.line.article')}</th>
              <th className={subTh}>{t('salesOrders.line.family')}</th>
              <th className={`${subTh} text-right`}>{t('salesOrders.line.qtyOrdered')}</th>
              <th className={subTh}>{t('salesOrders.line.um')}</th>
              <th className={`${subTh} text-right`}>{t('salesOrders.line.unitPrice')}</th>
              <th className={`${subTh} text-right`}>{t('salesOrders.line.shipDate')}</th>
            </tr>
          </thead>
          <tbody>
            {lineas.map((l, i) => {
              // El overlay no trae inventtransid —el pedido aún no está en
              // Fabric—, así que la identifica su nº de línea dentro del pedido.
              const clave = claveFila(clavePedido, 'linea', l.numLinea)
              return (
              <FilaMarcable
                key={`${l.numLinea ?? ''}-${i}`}
                marcas={marcas}
                clave={clave}
                dark={dark}
                className={`border-t ${divider}`}
              >
                <MarcaCelda marcas={marcas} clave={clave} dark={dark} densa />
                <td className={`${subTd} tabular-nums ${mutedTxt}`}>{l.numLinea ?? '—'}</td>
                <td className={subTd}>
                  <div className="max-w-[180px]">
                    <div className="font-semibold truncate text-[11px]" title={fmtArt(l.articulo)}>
                      {fmtArt(l.articulo) || '—'}
                    </div>
                    <div className={`text-[9px] truncate ${mutedTxt}`} title={l.descripcion}>
                      {l.descripcion}
                    </div>
                  </div>
                </td>
                <td className={subTd}>
                  <div className="text-[10px]">{l.familia ?? '—'}</div>
                  {l.subfamilia && <div className={`text-[9px] ${mutedTxt}`}>{l.subfamilia}</div>}
                </td>
                <td className={`${subTd} text-right tabular-nums`}>{fmtN(l.cantidad)}</td>
                <td className={`${subTd} ${mutedTxt}`}>{l.um ?? '—'}</td>
                <td className={`${subTd} text-right tabular-nums`}>
                  {l.precioUnitario
                    ? l.precioUnitario.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
                    : '—'}
                </td>
                <td className={`${subTd} text-right tabular-nums`}>{fmtDate(l.fechaEnvio)}</td>
              </FilaMarcable>
              )
            })}
          </tbody>
        </table>
      </div>
    </SubTableWrapper>
  )
}

// ── Fabric: pedido consolidado, con facturas ─────────────────────────────────

function LineasFabric({
  salesid, dark, marcas, clavePedido, terminos, onVerAdjuntosFactura, puedeCrearCarga,
  filtroEstadoCarga,
}: {
  salesid: string
  dark: boolean
  marcas: MarcasFilas
  clavePedido: string
  terminos?: string[]
  onVerAdjuntosFactura: (recid: string, factura: string) => void
  puedeCrearCarga?: boolean
  filtroEstadoCarga?: FiltroEstadoCarga
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const queryClient = useQueryClient()

  // Las consultas y los derivados viven en el hook, compartidos con la pantalla
  // dedicada del pedido: mismas queryKeys, misma caché y —lo que importa— los
  // mismos números en las dos vistas.
  const {
    lineas, cargas, solicitudes, pickingPorLinea, lineaPorTransid,
    descripcionPorTransid, lanzamientoDe, sinFacturar, qtySinLanzar,
    isLoading, isError,
  } = usePedidoVentaDetalle(salesid, { puedeCrearCarga })

  // Búsqueda por artículo: si el texto del buscador casa con algún artículo del
  // pedido, las facturas y cargas que lo contienen arrancan desplegadas y el
  // resto colapsado. El criterio vive en utils/filtroArticulo.ts, con tests.
  const coincide = useMemo(() => predicadoArticulos(terminos, fmtArt), [terminos, fmtArt])

  // Colapsado por defecto —en pedidos grandes son decenas de líneas que
  // enterrarían el resto del resumen—, salvo que contenga el artículo buscado.
  // null = el usuario no ha tocado nada, manda el automático.
  const [pendientesManual, setPendientesManual] = useState<boolean | null>(null)

  // Facturación: el pendiente que queda tras cada factura. No está en origen —
  // se deriva de las filas.
  const pendienteTrasFacturaMap = useMemo(() => pendienteTrasFactura(lineas), [lineas])

  // Tooltip de PEND. FACTURAR: conserva el acumulado factura a factura, que ya
  // no cabe en la columna al pasar ésta a mostrar lo que falta HOY.
  const tooltipFacturar = (l: PedidoVentaLinea) => t('salesOrders.line.pendingBillingTooltip', {
    pendiente: fmtQty(pendienteTrasFacturaMap.get(l) ?? l.qtySinFacturar),
    original:  fmtQty(l.qtyLinea),
  })

  // Tooltip de PEND. LANZAR: el crudo, más el exceso cuando se lanzó de más y la
  // celda tuvo que recortarse a 0.
  const tooltipLanzar = (l: PedidoVentaLinea) => {
    const { lanzado, exceso } = lanzamientoDe(l)
    return [
      t('salesOrders.line.pendingLaunchTooltip', {
        lanzado: fmtQty(lanzado),
        pedida:  fmtQty(l.qtyLinea),
      }),
      exceso ? t('salesOrders.line.pendingLaunchExcess', { exceso: fmtQty(exceso) }) : '',
    ].filter(Boolean).join('\n')
  }

  const subTh    =`px-2 py-1.5 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`
  const subTd    = `px-2 py-1.5 text-[11px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const mutedTxt = dark ? 'text-slate-500' : 'text-slate-400'
  const divider  = dark ? 'border-white/4' : 'border-slate-100'

  if (isLoading) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <Loader label={t('salesOrders.line.loading')} size={10} className="py-3" />
    </SubTableWrapper>
  )

  if (isError) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-10 py-3">
        <span className={`text-[11px] ${dark ? 'text-red-400' : 'text-red-600'}`}>
          {t('salesOrders.line.error')}
        </span>
      </div>
    </SubTableWrapper>
  )

  if (lineas.length === 0) return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-10 py-3">
        <span className={`text-[11px] ${mutedTxt}`}>{t('salesOrders.line.none')}</span>
      </div>
    </SubTableWrapper>
  )

  // Clave de negocio de una fila de línea: la transacción de inventario (que casa
  // 1:1 con la línea de venta) MÁS la factura, porque la misma línea aparece una
  // vez en cada factura suya y otra en la banda de lo pendiente (factura vacía).
  // Nunca el índice: al ordenar las facturas o cambiar de página, las marcas se
  // moverían de fila.
  const claveLinea = (l: PedidoVentaLinea) =>
    claveFila(clavePedido, 'linea', l.inventtransid ?? l.numLinea ?? '', l.factura ?? '')

  const renderLinea = (l: PedidoVentaLinea, key: string, esSinFacturar: boolean) => {
    const clave = claveLinea(l)
    return (
    // Resaltada si es lo que se está buscando: en una factura con muchas líneas,
    // desplegarla no basta — hay que poder ver de un golpe cuál es.
    <FilaMarcable
      key={key}
      marcas={marcas}
      clave={clave}
      dark={dark}
      className={`border-t ${divider} ${coincide?.(l.articulo, l.descripcion) ? filaResaltada(dark) : ''}`}
    >
      <MarcaCelda marcas={marcas} clave={clave} dark={dark} densa />
      <td className={`${subTd} tabular-nums ${mutedTxt}`}>{l.numLinea ?? '—'}</td>
      <td className={subTd}>
        <div className="max-w-[180px]">
          <div className="font-semibold truncate text-[11px] flex items-center gap-1.5" title={fmtArt(l.articulo)}>
            {fmtArt(l.articulo) || '—'}
            {!!l.esArmaf && (
              <span className="shrink-0 text-[8px] font-black px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20 tracking-wider">
                ARMAF
              </span>
            )}
          </div>
          <div className={`text-[9px] truncate ${mutedTxt}`} title={l.descripcion}>{l.descripcion}</div>
        </div>
      </td>
      <td className={subTd}>
        <div className="text-[10px] truncate" title={l.familia}>{l.familia ?? '—'}</div>
        {l.subfamilia && (
          <div className={`text-[9px] truncate ${mutedTxt}`} title={l.subfamilia}>{l.subfamilia}</div>
        )}
      </td>
      {/* Cantidad de la línea completa, en las DOS bandas: es la referencia
          contra la que se leen las demás cifras de la fila. No existe en origen
          —se deriva sumando las filas de la línea—; la limitación conocida está
          en docs/superpowers/specs/2026-08-18-pedidos-venta-columnas-cantidad. */}
      <td className={`${subTd} text-right tabular-nums ${mutedTxt}`}>
        {fmtN(l.qtyLinea)}
      </td>
      <td className={`${subTd} text-right tabular-nums`}>{fmtN(l.qtyPedida)}</td>
      {/* Avance, con la métrica que importa en cada banda:
           · sin facturar → picking, lo que el almacén ya ha preparado. Una línea
             puede estar picada al 73% y servida al 0% mientras no se expide.
           · facturada    → servido, que es lo que esa entrega ya entregó.
          El tooltip dice siempre cuál de las dos se está mostrando. */}
      <td className={subTd}>
        {(() => {
          if (esSinFacturar) {
            const p = l.inventtransid ? pickingPorLinea.get(l.inventtransid) : undefined
            if (!p) return <span className={mutedTxt}>—</span>
            return (
              <PickingBar
                pct={p.pct}
                dark={dark}
                compact
                title={[
                  t('salesOrders.line.progressPicking'),
                  t('salesOrders.picking.lineaTooltip', {
                    picking: fmtQty(p.picking),
                    solicitada: fmtQty(p.solicitada),
                    pendiente: fmtQty(p.pendiente),
                  }),
                ].join('\n')}
              />
            )
          }
          const pct = l.qtyPedida > 0 ? (l.qtyServida / l.qtyPedida) * 100 : 0
          return (
            <PickingBar
              pct={pct}
              dark={dark}
              compact
              title={[
                t('salesOrders.line.progressServed'),
                t('salesOrders.line.servedTooltip', {
                  servida: fmtQty(l.qtyServida),
                  pedida: fmtQty(l.qtyPedida),
                  // El mismo número que la columna PEND. FACTURAR: antes usaba
                  // el crudo de la fila y podían contradecirse.
                  pendiente: fmtQty(l.qtySinFacturar),
                }),
              ].join('\n')}
            />
          )
        })()}
      </td>
      <td className={`${subTd} text-right tabular-nums ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
        {fmtN(l.qtyServida)}
      </td>
      {/* Pendiente, con la métrica que importa en cada banda:
           · facturada    → lo que le queda a la LÍNEA por facturar HOY. El mismo
             número en todas sus facturas; el acumulado se va al tooltip.
           · sin facturar → lo que aún no se ha lanzado a ninguna carga.
          El cero se escribe "0" y no "—": aquí «no queda nada pendiente» es
          información, y la raya se leería como «sin dato». */}
      <td
        className={`${subTd} text-right tabular-nums font-semibold`}
        title={esSinFacturar ? tooltipLanzar(l) : tooltipFacturar(l)}
      >
        {fmtQty(esSinFacturar ? lanzamientoDe(l).pendiente : l.qtySinFacturar)}
      </td>
      <td className={`${subTd} ${mutedTxt}`}>{l.um ?? '—'}</td>
      <td className={`${subTd} text-right tabular-nums`}>
        {l.precioUnitario ? l.precioUnitario.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '—'}
      </td>
      <td className={`${subTd} text-right tabular-nums`}>
        {l.descuento ? fmtPct(l.descuento) : '—'}
      </td>
      <td className={`${subTd} text-right tabular-nums`}>
        {l.importeNeto ? fmtEur(l.importeNeto) : '—'}
      </td>
      <td className={`${subTd} text-right tabular-nums font-semibold`}>
        {l.importePendiente ? fmtEur(l.importePendiente) : '—'}
        {l.moneda && l.moneda !== 'EUR' && (
          <div className={`text-[9px] font-normal ${mutedTxt}`}>{l.moneda}</div>
        )}
      </td>
      <td className={subTd}>
        {l.estadoLinea ? (
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
            ESTADO_COLOR[l.estadoLinea] ?? (dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-500')
          }`}>
            {t('salesOrders.status.' + l.estadoLinea, { defaultValue: l.estadoLinea })}
          </span>
        ) : '—'}
      </td>
      <td className={`${subTd} text-right tabular-nums`}>{fmtDate(l.fechaEntrega)}</td>
      <td className={`${subTd} text-right tabular-nums`}>{fmtDate(l.fechaEnvio)}</td>
    </FilaMarcable>
    )
  }

  // Mismas columnas en los dos sitios donde salen líneas, pero las dos que
  // cambian de significado según la banda cambian también de nombre: es lo que
  // permite leer una fila sin conocer el modelo de datos por debajo.
  const cabeceraLineas = (esSinFacturar: boolean) => (
    <thead>
      <tr className={`border-b ${divider}`}>
        <CabeceraMarca />
        <th className={subTh}>{t('salesOrders.line.lineNum')}</th>
        <th className={subTh}>{t('salesOrders.line.article')}</th>
        <th className={subTh}>{t('salesOrders.line.family')}</th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.qtyOriginal')}</th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.qtyOrdered')}</th>
        <th className={subTh}>
          {t(esSinFacturar
            ? 'salesOrders.line.progressPickingCol'
            : 'salesOrders.line.progressServedCol')}
        </th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.qtyServed')}</th>
        <th className={`${subTh} text-right`}>
          {t(esSinFacturar
            ? 'salesOrders.line.qtyPendingLaunch'
            : 'salesOrders.line.qtyPendingBilling')}
        </th>
        <th className={subTh}>{t('salesOrders.line.um')}</th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.unitPrice')}</th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.discount')}</th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.netAmount')}</th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.pendingAmount')}</th>
        <th className={subTh}>{t('salesOrders.line.status')}</th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.deliveryDate')}</th>
        <th className={`${subTh} text-right`}>{t('salesOrders.line.shipDate')}</th>
      </tr>
    </thead>
  )

  // table-fixed + colgroup: cada factura pinta su propia tabla, y con el ancho
  // automático el navegador la dimensiona por su contenido — descripciones más
  // largas movían las columnas y las tablas no cuadraban entre sí. Con anchos
  // declarados, todas quedan alineadas.
  //
  // Los 1.582 px son el MÍNIMO, no el ancho: la tabla va con `w-full`, así que en
  // pantallas anchas se estira y `table-fixed` reparte el sobrante entre las
  // columnas en proporción a estos anchos. Para que la tabla de una factura y la
  // de la banda ámbar sigan cuadrando, las dos cuelgan de un contenedor con el
  // mismo padding.
  const anchosLineas = (
    <colgroup>
      {/* Canalón de marcas: una columna más de la rejilla, de 24 px. */}
      <col className="w-[24px]" />
      <col className="w-[44px]" />
      <col className="w-[210px]" />
      <col className="w-[130px]" />
      <col className="w-[84px]" />
      <col className="w-[84px]" />
      <col className="w-[112px]" />
      <col className="w-[84px]" />
      <col className="w-[88px]" />
      <col className="w-[52px]" />
      <col className="w-[92px]" />
      <col className="w-[62px]" />
      <col className="w-[104px]" />
      <col className="w-[104px]" />
      <col className="w-[116px]" />
      <col className="w-[96px]" />
      <col className="w-[96px]" />
    </colgroup>
  )

  const tablaLineas = (ls: PedidoVentaLinea[], esSinFacturar: boolean) => (
    <table className="w-full min-w-[1582px] table-fixed border-collapse text-left">
      {anchosLineas}
      {cabeceraLineas(esSinFacturar)}
      <tbody>
        {ls.map((l, i) => renderLinea(l, `${l.numLinea ?? ''}-${i}`, esSinFacturar))}
      </tbody>
      {/* Un total por banda y no uno para todo el pedido: la cantidad de línea y
          el pendiente de facturar son cifras de nivel LÍNEA, repetidas en cada
          factura, y un pie único las contaría una vez por cada una. */}
      <tfoot>
        <SubTotalsRow
          dark={dark}
          rows={ls}
          // El canalón de marcas es una columna más de la rejilla: sin su hueco
          // el pie se desplazaría una celda respecto a la cabecera.
          columnas={[{ key: '__marca__' }, ...columnasPieVenta({
            esSinFacturar,
            pendiente: (l) => lanzamientoDe(l).pendiente,
            fmtCantidad: fmtQty,
            fmtImporte: fmtEur,
          })]}
        />
      </tfoot>
    </table>
  )

  // Las facturadas viven dentro de su factura; aquí abajo queda lo pendiente
  // (`sinFacturar` y `qtySinLanzar` salen del hook, compartidos con el detalle).
  // Los dos totales de la cabecera cuadran con las columnas de dentro por
  // construcción: qtyPedida es lo que pinta QTY PED., y el pendiente de lanzar se
  // suma ya recortado a 0, igual que la celda.
  const qtySinFacturar = sinFacturar.reduce((s, l) => s + l.qtyPedida, 0)
  const impPend = sinFacturar.reduce((s, l) => s + (l.importePendiente ?? 0), 0)

  // La banda ámbar se abre sola si lo buscado está aún sin facturar, que en un
  // pedido abierto es donde más probable es que esté.
  const pendientesAbierto = pendientesManual
    ?? (!!coincide && sinFacturar.some(l => coincide(l.articulo, l.descripcion)))

  return (
    <SubTableWrapper dark={dark} colSpan={TOTAL_COLS}>
      <div className="pl-8 pr-4 py-2 overflow-x-auto">
        <FacturasPedido
          dark={dark}
          lineas={lineas}
          onVerAdjuntosFactura={onVerAdjuntosFactura}
          coincide={coincide}
          renderLineas={(filas) => tablaLineas(filas, false)}
          marcas={marcas}
          clavePedido={clavePedido}
        />
        <CargasPedido
          dark={dark}
          marcas={marcas}
          clavePedido={clavePedido}
          cargas={cargas}
          lineaPorTransid={lineaPorTransid}
          descripcionPorTransid={descripcionPorTransid}
          coincide={coincide}
          filtroEstado={filtroEstadoCarga}
          empresa={lineas[0]?.dataareaid}
          numPedido={salesid}
          lineas={lineas}
          solicitudes={solicitudes}
          puedeCrear={puedeCrearCarga}
          onSolicitada={() => {
            // La carga no existe todavía, pero la solicitud sí: hay que releerla
            // para que aparezca su fila y para que el disponible baje.
            queryClient.invalidateQueries({ queryKey: ['venta-solicitudes-carga', salesid] })
            queryClient.invalidateQueries({ queryKey: ['venta-cargas', salesid] })
          }}
        />
        {sinFacturar.length > 0 && (
          <div className={`rounded-lg overflow-hidden ${dark ? 'bg-amber-500/[0.04]' : 'bg-amber-50/40'}`}>
            {/* Lo que ni se ha entregado ni facturado. Las líneas facturadas ya
                no salen aquí: viven dentro de su factura, en el bloque de arriba.
                Los dos distintivos van juntos porque en estos datos es el mismo
                conjunto: la cantidad servida sólo se rellena al facturar. */}
            <button
              type="button"
              onClick={() => setPendientesManual(!pendientesAbierto)}
              aria-expanded={pendientesAbierto}
              className={`w-full flex items-center gap-2 flex-wrap px-2 py-2 text-left cursor-pointer transition-colors ${
                dark ? 'bg-amber-500/[0.07] hover:bg-amber-500/[0.12]' : 'bg-amber-50/70 hover:bg-amber-100/70'
              }`}
            >
              <svg
                className={`w-3 h-3 shrink-0 transition-transform duration-150 ${pendientesAbierto ? 'rotate-90' : ''} ${mutedTxt}`}
                viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              >
                <path d="M5 11l4-4-4-4" />
              </svg>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${
                dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700'
              }`}>
                {t('salesOrders.invoice.undelivered')}
              </span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide ${
                dark ? 'bg-white/8 text-slate-300' : 'bg-slate-200/80 text-slate-600'
              }`}>
                {t('salesOrders.invoice.unbilled')}
              </span>
              <span className={`text-[10px] ${mutedTxt}`}>
                · {t('salesOrders.invoice.lines', { count: sinFacturar.length })}
              </span>
              {/* Los dos números que deciden si merece la pena abrir la banda:
                  cuánto falta por facturar y cuánto ni se ha lanzado todavía. */}
              <span className={`ml-auto flex items-center gap-2 text-[10px] ${dark ? 'text-slate-200' : 'text-slate-700'}`}>
                <span>
                  {t('salesOrders.invoice.unbilledQty')}{' '}
                  <span className="tabular-nums font-semibold">{fmtQty(qtySinFacturar)}</span>
                </span>
                <span className={mutedTxt}>·</span>
                <span>
                  {t('salesOrders.invoice.unlaunchedQty')}{' '}
                  <span className="tabular-nums font-semibold">{fmtQty(qtySinLanzar)}</span>
                </span>
                {impPend ? <span className="tabular-nums font-semibold">{fmtEur(impPend)}</span> : null}
              </span>
            </button>
            {/* Mismo padding que el desplegable de una factura, para que las dos
                tablas tengan exactamente el mismo ancho y sus columnas cuadren. */}
            {pendientesAbierto && (
              <div className="px-3 pb-2 pl-8 overflow-x-auto">
                {tablaLineas(sinFacturar, true)}
              </div>
            )}
          </div>
        )}
      </div>
    </SubTableWrapper>
  )
}
